import { useEffect, type ReactNode } from "react";
import type { PlanSummary } from "@edu/shared";
import type { AnswerableView } from "./answerable.ts";
import { PlanSource } from "./PlanSource.tsx";
import { useTeacherSubjects } from "../../../lib/subjects.ts";
import { topicSubjectsOf } from "../../exercises/topics/subject.ts";
import {
  brokenTopicsNote,
  emptyTopicsNote,
  sourceLabel,
  sourceOfSubject,
  subjectOfSource,
  type SourceKind,
  type TopicSubject,
} from "./subjects.ts";
import { TopicPicks } from "../../exercises/topics/TopicPicks.tsx";
import type { TopicPicks as TopicPicksState } from "../../exercises/topics/use-topic-picks.ts";

/**
 * Блок «Задания»: переключатель источника и выбранная ветка (5.5
 * docs/homework-phase2-design.md).
 *
 * ⚠️ Источник ровно ОДИН, смешения нет (решение владельца от 17.08.2026). Это
 * снимает сразу три вещи: общий счётчик заданий, считаемый из двух мест; правило
 * порядка «сначала заготовка, потом темы», которого никто не заказывал; и
 * объяснение на экране, почему часть заданий у всех одинаковая, а часть у
 * каждого своя. Цена названа честно: «три задания с воркшита плюс пять на дроби»
 * это две домашки, два срока и две строки в списке у ученика.
 *
 * ⚠️ С английской волной источников стало три, и предметы стоят ОТДЕЛЬНЫМИ
 * радиокнопками, а не вторым переключателем внутри «По темам». Причина та же,
 * по которой источник один: одна работа собирается из одного источника, а у
 * двух предметов это ещё и «одна работа по одному предмету» (`homework.ts`).
 * Вложенный переключатель предмета обещал бы смешанную домашку, которой не
 * бывает, и прятал бы английский за лишним нажатием у того, кто пришёл именно
 * за ним.
 *
 * ⚠️ Радиокнопки, а не вкладки: все три половины обязаны быть видны как выбор.
 * Подпись «появится вместе с автогенератором задач» снята 18.08.2026 шагом Б4,
 * вместе с приездом самой ветки. Осталась вторая, ДРУГАЯ причина выключения:
 * справочник тем не загрузился. Путать их нельзя, это разные обещания.
 *
 * Сам юнион `SourceKind` живёт в `./subjects.ts`: он выведен из контракта
 * домашки, и держать его в файле с разметкой значило бы описывать контракт
 * рядом с кнопками.
 */

export function ExerciseSource(props: {
  kind: SourceKind;
  onKind: (kind: SourceKind) => void;
  busy: boolean;
  plans: PlanSummary[];
  planId: string;
  planLoaded: boolean;
  answerable: AnswerableView[];
  excluded: ReadonlySet<string>;
  onPlanId: (id: string) => void;
  onToggle: (elementId: string) => void;
  /** Справочники веток «По темам»: по одному состоянию на предмет. */
  topics: Readonly<Record<TopicSubject, TopicPicksState>>;
  /**
   * Картинки выбранной заготовки (1.2 docs/homework-scratch-design.md,
   * источник 3). Стоят рядом с её заданиями, а не в блоке «Материал»: человек
   * выбирает заготовку здесь и её содержимое ждёт увидеть тут же.
   */
  planImages?: ReactNode;
}) {
  /*
    Ветка «По темам» выключается, только когда известно, что тем не будет:
    справочник не загрузился либо приехал пустым. Пока он ГРУЗИТСЯ, радиокнопка
    включена, и это не недосмотр, а сценарий приёмки 3: переключиться можно
    сразу, а «Загрузка тем…» показывает сам блок. Выключать на время загрузки
    значило бы отнимать выбор у того, кто быстрее сети.

    ⚠️ Комментарий здесь раньше говорил «недоступна, пока справочник не приехал»
    и расходился с кодом (находка ревью 18.08.2026). Не «чинить» код по нему.
  */
  /**
   * Ветки «По темам» только по предметам, которые ведёт преподаватель («Мои
   * предметы»). Перечни отказов ниже считаются по тому же списку: назвать в
   * них предмет, радиокнопки которого нет, значило бы объяснять невидимое.
   */
  const shown = topicSubjectsOf(useTeacherSubjects());
  const topicsOff = (subject: TopicSubject): boolean => {
    const state = props.topics[subject];
    return state.failed || state.topics?.length === 0;
  };
  /** Предмет открытой ветки. `null` означает «открыта заготовка». */
  const asked = subjectOfSource(props.kind);
  /**
   * ⚠️ Открытая ветка предмета, радиокнопки которого нет (восстановленный
   * черновик, ссылка с темами, «Мои предметы» поменяли в другой вкладке),
   * возвращается к заготовке. Показать чужую ветку без её радиокнопки значило бы
   * собрать домашку из источника, который человек выбрать уже не может.
   */
  const hidden = asked !== null && !shown.includes(asked);
  const openSubject = hidden ? null : asked;
  const { onKind } = props;
  useEffect(() => {
    if (hidden) onKind("plan");
  }, [hidden, onKind]);
  /*
    Причина выключения называется у СОСЕДНЕЙ ветки: открытая объясняет себя сама
    (`TopicPicks` из `features/exercises/topics/` печатает и отказ со своей
    кнопкой «Повторить», и пустой справочник), а выключенная радиокнопка без
    объяснения читается как поломка.
    Поэтому предмет, который сейчас открыт, из обоих перечней выброшен, и в
    ветке «Из заготовки» перечни называют оба предмета.

    ⚠️ Причин две, и они РАЗНЫЕ: «не загрузилось» лечится повтором, «тем нет»
    не лечится ничем. Одна строка на обе означала бы кнопку «Повторить» там, где
    повторять нечего. Пустой справочник сегодня недостижим (генераторы есть у
    обоих предметов), но строка написана заранее: третий предмет приедет в карту
    раньше, чем в реестр генераторов.
  */
  const offFor = (why: (state: TopicPicksState) => boolean): TopicSubject[] =>
    shown.filter((subject) => subject !== openSubject && why(props.topics[subject]));
  const broken = offFor((state) => state.failed);
  const brokenNote = brokenTopicsNote(broken);
  const emptyNote = emptyTopicsNote(offFor((state) => state.topics?.length === 0));

  return (
    <section className="card">
      <h2>Задания</h2>

      <div className="assign__sources">
        <label className="form__check">
          <input
            type="radio"
            name="hw-source"
            checked={props.kind === "plan"}
            disabled={props.busy}
            onChange={() => props.onKind("plan")}
          />
          <span>Из заготовки</span>
        </label>
        {shown.map((subject) => (
          <label key={subject} className={topicsOff(subject) ? "muted" : undefined}>
            <input
              type="radio"
              name="hw-source"
              checked={props.kind === sourceOfSubject(subject)}
              disabled={props.busy || topicsOff(subject)}
              onChange={() => props.onKind(sourceOfSubject(subject))}
            />
            <span>{sourceLabel(subject)}</span>
          </label>
        ))}
      </div>

      {openSubject === null ? (
        <>
          <PlanSource
            plans={props.plans}
            planId={props.planId}
            planLoaded={props.planLoaded}
            answerable={props.answerable}
            excluded={props.excluded}
            onPlanId={props.onPlanId}
            onToggle={props.onToggle}
          />
          {props.planImages}
        </>
      ) : (
        <TopicPicks state={props.topics[openSubject]} busy={props.busy} />
      )}

      {/*
        Отказ справочника показывается и в чужой ветке: иначе выключенная
        радиокнопка стоит без объяснения, а выключенный контрол без объяснения
        читается как поломка
      */}
      {brokenNote !== null && (
        <p className="muted">
          {brokenNote}{" "}
          <button
            type="button"
            className="btn btn--quiet"
            onClick={() => {
              for (const subject of broken) props.topics[subject].reload();
            }}
          >
            Повторить
          </button>
        </p>
      )}
      {emptyNote !== null && <p className="muted">{emptyNote}</p>}
    </section>
  );
}
